import React, { useState, useRef, useEffect } from 'react';
import { useAppStore } from '../store/app-store';
import { getUnifiedChatSystem, initializeUnifiedChatSystem } from '../lib/unified-chat-system';
import { parseSkillCommand } from '../lib/skills';
import { useConversationCompactor } from '../hooks/useConversationCompactor';
import FileSystemAccess from './FileSystemAccess';
import TokenStats from './TokenStats';
import SystemStatus from './SystemStatus';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { nanoid } from 'nanoid';

export function UnifiedChat() {
  const {
    getActiveConversation,
    addMessage,
    selectedModel,
    settings,
    incrementStatistic
  } = useAppStore();

  const conversation = getActiveConversation();
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [toolCalls, setToolCalls] = useState([]);
  const [activeAgents, setActiveAgents] = useState([]);
  const [systemReady, setSystemReady] = useState(false);
  const [initError, setInitError] = useState(null);
  const messagesEndRef = useRef(null);
  const chatSystemRef = useRef(null);

  // Conversation compactor
  const { compactIfNeeded } = useConversationCompactor();

  // Initialize chat system
  useEffect(() => {
    // Wait for model client to be ready (initialized in App.jsx)
    const initTimeout = setTimeout(() => {
      try {
        const system = initializeUnifiedChatSystem();
        const ready = system.initialize();

        if (ready) {
          chatSystemRef.current = system;
          setSystemReady(true);
          console.log('[UnifiedChat] ✅ System ready');
        } else {
          setInitError('Model client not ready. Check Cloudflare configuration.');
          console.warn('[UnifiedChat] ⚠️ System not ready');
        }
      } catch (error) {
        console.error('[UnifiedChat] ❌ Init error:', error);
        setInitError(error.message);
      }
    }, 500); // Give App.jsx time to initialize model client

    return () => clearTimeout(initTimeout);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation?.messages, progress]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || !conversation || isLoading) return;

    const userMessage = {
      id: nanoid(),
      role: 'user',
      content: input.trim(),
      timestamp: Date.now()
    };

    addMessage(conversation.id, userMessage);
    const userInput = input.trim();
    setInput('');
    setIsLoading(true);
    setProgress('');
    setToolCalls([]);
    setActiveAgents([]);

    try {
      const chatSystem = chatSystemRef.current;
      if (!chatSystem || !systemReady) {
        throw new Error('Chat system not ready yet. Please wait...');
      }

      // Auto-compact conversation if needed
      if (compactIfNeeded) {
        setProgress('Checking token usage...');
        const compactResult = await compactIfNeeded((msg) => setProgress(msg));
        if (compactResult.compacted) {
          setProgress(`✅ Saved ${compactResult.stats.percentSaved}% tokens`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Show message briefly
        }
      }

      // Check if it's a skill command
      const skillCommand = parseSkillCommand(userInput);

      let responseType = 'direct';
      if (skillCommand) {
        responseType = 'skill';
        setProgress(`Executing skill: ${skillCommand.skill.name}...`);
      } else if (chatSystem.shouldUseAgent(userInput)) {
        responseType = 'agent';
        setProgress('Detecting appropriate agent...');
      }

      // Process message with full capabilities
      const result = await chatSystem.processMessage(userInput, {
        model: selectedModel || 'deepseek-v4-pro:cloud',
        temperature: settings.temperature,
        maxTokens: settings.maxTokens,
        enableTools: true,
        enableAgents: true,
        enableSkills: true,
        onProgress: (msg) => {
          setProgress(msg);
        },
        onToolCall: (tool, params) => {
          setToolCalls(prev => [...prev, { tool, params, status: 'executing' }]);
        },
        onAgentSpawn: (agentName, task) => {
          setActiveAgents(prev => [...prev, { name: agentName, task }]);
        }
      });

      // Create assistant message
      const assistantMessage = {
        id: nanoid(),
        role: 'assistant',
        content: result.content || 'No response generated',
        timestamp: Date.now(),
        type: result.type,
        agent: result.agent,
        skill: result.skill,
        turns: result.turns,
        toolCallsCount: result.toolCalls || toolCalls.length,
        model: selectedModel,
        provider: result.provider
      };

      addMessage(conversation.id, assistantMessage);
      incrementStatistic('totalMessages');

    } catch (error) {
      console.error('Error processing message:', error);

      const errorMessage = {
        id: nanoid(),
        role: 'assistant',
        content: `❌ Error: ${error.message}`,
        timestamp: Date.now(),
        isError: true
      };

      addMessage(conversation.id, errorMessage);
    } finally {
      setIsLoading(false);
      setProgress('');
      setToolCalls([]);
      setActiveAgents([]);
    }
  };

  const renderMessage = (message) => {
    const html = marked.parse(message.content);
    const sanitized = DOMPurify.sanitize(html);

    return (
      <div
        key={message.id}
        className={`message ${message.role} ${message.isError ? 'error' : ''}`}
      >
        <div className="message-header">
          <span className="message-role">
            {message.skill && `⚡ Skill: ${message.skill} | `}
            {message.agent && `🤖 Agent: ${message.agent} | `}
            {message.role}
          </span>
          <span className="message-time">
            {new Date(message.timestamp).toLocaleTimeString()}
          </span>
        </div>

        <div
          className="message-content"
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />

        {message.turns > 1 && (
          <div className="message-meta">
            🔄 {message.turns} reasoning turns
          </div>
        )}

        {message.toolCallsCount > 0 && (
          <div className="message-meta">
            🛠️ {message.toolCallsCount} tool calls executed
          </div>
        )}

        {message.model && (
          <div className="message-meta">
            🤖 Model: {message.model} {message.provider && `(${message.provider})`}
          </div>
        )}
      </div>
    );
  };

  if (!conversation) {
    return (
      <div className="chat-empty">
        <h2>🚀 Unified Development Assistant</h2>
        <p>Create a new conversation to get started</p>
        <div className="feature-grid">
          <div className="feature-item">
            <h3>🤖 53 Models</h3>
            <p>Ollama + Cloudflare</p>
          </div>
          <div className="feature-item">
            <h3>🛠️ 20+ Tools</h3>
            <p>MCP integrated</p>
          </div>
          <div className="feature-item">
            <h3>👥 12 Agents</h3>
            <p>GSD + DevTeam</p>
          </div>
          <div className="feature-item">
            <h3>⚡ 10 Skills</h3>
            <p>/gsd:*, /review, /test</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      {/* System Status */}
      <SystemStatus />

      {/* File System Access */}
      <FileSystemAccess />

      {/* Token Stats */}
      {conversation.messages.length > 0 && (
        <TokenStats onCompact={setProgress} />
      )}

      {/* System status indicator */}
      {!systemReady && initError && (
        <div className="system-error">
          ⚠️ {initError}
        </div>
      )}

      {!systemReady && !initError && (
        <div className="system-loading">
          ⏳ Initializing chat system...
        </div>
      )}

      <div className="messages-container">
        {conversation.messages.map(renderMessage)}

        {progress && (
          <div className="progress-indicator">
            💭 {progress}
          </div>
        )}

        {activeAgents.length > 0 && (
          <div className="active-agents">
            <strong>🤖 Active Agents:</strong>
            {activeAgents.map((agent, idx) => (
              <div key={idx} className="agent-badge">
                {agent.name}
              </div>
            ))}
          </div>
        )}

        {toolCalls.length > 0 && (
          <div className="tool-execution">
            <strong>🛠️ Executing tools:</strong>
            {toolCalls.map((tc, idx) => (
              <div key={idx} className="tool-badge">
                {tc.tool}
              </div>
            ))}
          </div>
        )}

        {isLoading && !progress && (
          <div className="message assistant loading">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form className="input-form" onSubmit={handleSubmit}>
        <div className="input-row">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder={systemReady ? "Try: /gsd:new-project 'Your idea' or ask anything..." : "Initializing..."}
            disabled={isLoading || !systemReady}
            rows={3}
          />
          <button type="submit" disabled={isLoading || !input.trim() || !systemReady}>
            {isLoading ? '⏳' : systemReady ? '➤' : '⌛'}
          </button>
        </div>

        <div className="capabilities-row">
          <div className="capability-hint">
            💡 <strong>Skills:</strong> /gsd:*, /review, /test, /docs, /search, /analyze
          </div>
          <div className="capability-hint">
            🤖 <strong>Model:</strong> {selectedModel?.split(':')[0] || 'Not selected'}
          </div>
        </div>
      </form>
    </div>
  );
}
